import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLang } from '../i18n/translations';
import api from '../lib/api';
import { CITIES } from '../lib/constants';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Search, MapPin, ChevronRight, RotateCcw } from 'lucide-react';

export default function Ponuda() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState('');
  const [city, setCity] = useState('all');
  const [typeId, setTypeId] = useState(params.get('tip') || 'all');
  const [catId, setCatId] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [o, t, c] = await Promise.all([api.get('/offers'), api.get('/types'), api.get('/categories')]);
        setOffers(o.data);
        setTypes(t.data);
        setCategories(c.data);
      } catch (err) {
        console.error('Greška pri učitavanju:', err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const tip = params.get('tip');
    if (tip) setTypeId(tip);
  }, [params]);

  const cats = useMemo(
    () => (typeId === 'all' ? categories : categories.filter((c) => c.typeId === typeId)),
    [categories, typeId]
  );

  const filtered = useMemo(() => offers.filter((o) => {
    if (typeId !== 'all' && o.typeId !== typeId) return false;
    if (catId !== 'all' && o.categoryId !== catId) return false;
    if (city !== 'all' && o.city !== city) return false;
    if (q && !(`${o.title} ${o.shortDesc} ${o.city}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [offers, typeId, catId, city, q]);

  const reset = () => { setQ(''); setCity('all'); setTypeId('all'); setCatId('all'); setParams({}); };

  return (
    <div className="bg-[#f8f9fd] min-h-screen">
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-[#0b1a3d] mb-2" data-testid="ponuda-title">{t('offer')}</h1>
          <p className="text-slate-500 max-w-2xl">
            Kompletna turistička ponuda Srednjobosanskog kantona – znamenitosti, smještaj, gastronomija, priroda i aktivnosti.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* FILTERI */}
        <div className="bg-white rounded-xl border border-slate-100 p-4 mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ponuda-filters">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} data-testid="ponuda-search"
              className="pl-9 border-slate-200 focus-visible:ring-[#001752]" />
          </div>
          <Select value={typeId} onValueChange={(v) => { setTypeId(v); setCatId('all'); }}>
            <SelectTrigger className="border-slate-200" data-testid="filter-type"><SelectValue placeholder={t('allTypes')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allTypes')}</SelectItem>
              {types.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={catId} onValueChange={setCatId}>
            <SelectTrigger className="border-slate-200" data-testid="filter-category"><SelectValue placeholder={t('allCategories')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allCategories')}</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="border-slate-200" data-testid="filter-city"><SelectValue placeholder={t('allCities')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allCities')}</SelectItem>
              {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-slate-500" data-testid="ponuda-count">{filtered.length} rezultata</p>
          <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500 hover:text-[#001752]" data-testid="ponuda-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> {t('clearFilter')}
          </Button>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 p-16 text-center text-slate-500" data-testid="ponuda-empty">
            {t('noResults')}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((o) => {
              const cat = categories.find((c) => c.id === o.categoryId);
              return (
                <Link key={o.id} to={`/ponuda/${o.id}`} data-testid={`offer-card-${o.id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-xl hover:shadow-slate-200/70 hover:-translate-y-1 transition-all duration-300">
                  <div className="h-52 overflow-hidden relative">
                    <img src={o.image} alt={o.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 bg-white/95 text-[#001752] text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {o.city}
                    </span>
                    {cat && (
                      <span className="absolute bottom-3 left-3 bg-[#001752]/90 text-white text-xs px-2.5 py-1 rounded-md">{cat.name}</span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0b1a3d] text-lg leading-snug mb-2">{o.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{o.shortDesc}</p>
                    <span className="inline-flex items-center text-sm font-semibold text-[#001752] mt-4 gap-1 group-hover:gap-2 transition-all duration-200">
                      {t('details')} <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
